import ky from 'ky'
import { z } from 'zod'

import { authSession } from '@/shared/api'

import { goauthBase } from './goauth-base'

/**
 * goauth endpoints, called directly. No discovery step — paths are fixed.
 *
 * This client is deliberately separate from `shared/api` and carries no 401
 * interceptor — a failed token call must not trigger the refresh it is part of.
 */
const goauth = ky.create({
  // `prefix`, not `baseUrl`: it joins by path segment, so the `/auth` suffix
  // survives instead of being replaced by the request path.
  prefix: `${goauthBase}/auth`,
  timeout: 20_000,
  retry: 0,
  throwHttpErrors: false,
})

/**
 * goauth's envelope is `{"data": …}` on success and `{"error": "…"}` on failure.
 * It has no `success` field — that is the api-service envelope, unwrapped in
 * `shared/api/client.ts`. The two are not the same shape.
 */
const unwrapData = (payload: unknown): unknown =>
  typeof payload === 'object' && payload !== null && 'data' in payload
    ? (payload as { data: unknown }).data
    : payload

/**
 * `user` and `must_change_password` ride along with `/auth/token` but not with
 * `/auth/password`, which returns a bare pair — hence optional.
 */
export const tokenSchema = z.object({
  access_token: z.string(),
  token_type: z.string(),
  expires_in: z.number(),
  expires_at: z.string().optional(),
  refresh_token: z.string(),
  must_change_password: z.boolean().optional(),
  user: z
    .object({
      id: z.string(),
      username: z.string().optional(),
      email: z.string().optional(),
      is_admin: z.boolean(),
    })
    .optional(),
})

export type TokenSet = z.infer<typeof tokenSchema>

/** The failure body carries one field, and it is an English sentence. */
const errorSchema = z.object({ error: z.string() })

export type TokenFailure = 'credentials' | 'blocked' | 'rate_limited' | 'unavailable'

export type TokenResult =
  | { ok: true; tokens: TokenSet }
  | { ok: false; reason: TokenFailure; message?: string }

/**
 * Classified by status, never by the message. goauth's `error` is human prose
 * with no stable code behind it, so matching on it would break the moment a
 * sentence is reworded.
 *
 * 400 joins 401 under `credentials`: the only way to send goauth a malformed
 * grant from this client is a request the user's input shaped.
 */
function reasonForStatus(status: number): TokenFailure {
  if (status === 429) return 'rate_limited'
  if (status === 403) return 'blocked'
  if (status === 401 || status === 400) return 'credentials'
  return 'unavailable'
}

async function requestToken(body: Record<string, string>): Promise<TokenResult> {
  let response: Response

  try {
    response = await goauth.post('token', { json: body })
  } catch (error) {
    // `throwHttpErrors` is off, so anything thrown here is transport: the
    // service is down, DNS failed, or the request timed out.
    console.error('token request failed', error)
    return { ok: false, reason: 'unavailable' }
  }

  const payload: unknown = await response.json().catch(() => null)

  if (!response.ok) {
    const parsed = errorSchema.safeParse(payload)
    return {
      ok: false,
      reason: reasonForStatus(response.status),
      message: parsed.success ? parsed.data.error : undefined,
    }
  }

  const parsed = tokenSchema.safeParse(unwrapData(payload))
  if (!parsed.success) {
    console.error('token response did not match its schema', parsed.error.issues)
    // A 2xx in the wrong shape is the server misbehaving, not a bad password.
    return { ok: false, reason: 'unavailable' }
  }

  return { ok: true, tokens: parsed.data }
}

/** Direct grant: the panel owns the form and hands the credentials over once. */
export const requestPasswordToken = (identifier: string, password: string) =>
  requestToken({ grant_type: 'password', identifier, password })

/** Rotating: every exchange spends the presented token and returns a new one. */
export const requestRefreshedToken = (refreshToken: string) =>
  requestToken({ grant_type: 'refresh_token', refresh_token: refreshToken })

export type RegistrationFailure = 'taken' | 'invalid' | 'closed' | 'rate_limited' | 'unavailable'

export type RegistrationResult =
  | { ok: true; tokens: TokenSet }
  | { ok: false; reason: RegistrationFailure; message?: string }

/**
 * Registration signs the new account in: goauth answers 201 with a full token
 * pair, so there is no second round trip to the password grant.
 *
 * A closed registration is a 404 — the route is only mounted when the mode is
 * open, and the usecase masks the closed case as "not found" besides. Either
 * way the client cannot tell them apart, and neither reading is actionable by
 * the visitor, so both become `closed`.
 */
export async function requestRegistration(
  username: string,
  password: string,
): Promise<RegistrationResult> {
  let response: Response

  try {
    response = await goauth.post('register', { json: { username, password } })
  } catch (error) {
    console.error('registration request failed', error)
    return { ok: false, reason: 'unavailable' }
  }

  const payload: unknown = await response.json().catch(() => null)

  if (!response.ok) {
    const parsed = errorSchema.safeParse(payload)
    return {
      ok: false,
      reason: registrationReason(response.status),
      message: parsed.success ? parsed.data.error : undefined,
    }
  }

  const parsed = tokenSchema.safeParse(unwrapData(payload))
  if (!parsed.success) {
    console.error('registration response did not match its schema', parsed.error.issues)
    return { ok: false, reason: 'unavailable' }
  }

  return { ok: true, tokens: parsed.data }
}

function registrationReason(status: number): RegistrationFailure {
  if (status === 409) return 'taken'
  if (status === 404) return 'closed'
  if (status === 429) return 'rate_limited'
  if (status === 400) return 'invalid'
  return 'unavailable'
}

/**
 * Ends the current session. goauth takes no body here — it reads the session id
 * from the `sid` claim of the bearer token, so this call needs the access token
 * and nothing else. Best effort: a session the provider already dropped still
 * has to end locally.
 */
export async function endProviderSession(): Promise<void> {
  const token = authSession.getToken()
  if (token === null) return

  await goauth
    .post('revoke', { headers: { Authorization: `Bearer ${token}` } })
    .catch((error: unknown) => {
      console.error('provider sign-out failed', error)
    })
}
